import { useGenerateCommunityPrompt } from '@communities/prompt-management/hooks';
import type { PromptGenerationResult } from '@communities/prompt-management/hooks';
import { formatErrorMessages } from '@core/adapters/errors/transformError';
import { ErrorDisplay } from '@core/components';
import { FEATURE_FLAGS } from '@core/domain';
import type { DomainError } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Dialog, LoadingOverlay } from '@soteria/design-system-ve';
import { useEffect, useState } from 'react';

import * as styles from './RegeneratePromptDialog.css';

interface RegeneratePromptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  communityId: string;
}

interface EnabledFlags {
  isUsernameEnabled: boolean;
}

function getNewPromptTypes(result: PromptGenerationResult | null, flags: EnabledFlags): string[] {
  const types: string[] = [];

  if (result?.classification.isNew) types.push('classification');
  if (result?.moderation.isNew) types.push('moderation');
  if (flags.isUsernameEnabled && result?.username.isNew) types.push('username');

  return types;
}

function formatSuccessMessage(isGenerating: boolean, newTypes: string[]): string {
  if (isGenerating) return 'Generating safety prompts...';

  if (newTypes.length > 0) {
    return `Safety prompt generation completed. New versions created for: ${newTypes.join(', ')}. Changes will be displayed shortly.`;
  }

  return 'Safety prompt generation completed. No changes detected, using existing cached prompts.';
}

function getCorrelationId(error: DomainError): string | undefined {
  return error.correlationId;
}

/**
 * RegeneratePromptDialog Component
 *
 * Displays a confirmation dialog for regenerating AI prompts
 */
function RegeneratePromptDialogComponent({ isOpen, onClose, communityId }: RegeneratePromptDialogProps) {
  // Simple state management like DeletePromptDialog
  const [isGenerating, setIsGenerating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [promptResult, setPromptResult] = useState<PromptGenerationResult | null>(null);

  // Feature flags for prompt type visibility
  const isUsernameEnabled = useIsFeatureEnabled(FEATURE_FLAGS.USERNAME_CLASSIFICATION);

  // Use dedicated hook for generating prompts
  const { generatePrompts, errors: hookErrors, resetErrors } = useGenerateCommunityPrompt();

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setIsGenerating(false);
      setShowSuccess(false);
      resetErrors();
    }
  }, [isOpen, resetErrors]);

  // Handle success state with timer
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (!isGenerating && showSuccess) {
      // Delay closing to show success message
      timer = setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 3000); // 3 second delay to ensure state is fully updated
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isGenerating, showSuccess, onClose]);

  const handleRegenerate = async () => {
    setIsGenerating(true);
    resetErrors();

    const result = await generatePrompts(communityId, {
      labelFeedback: [],
    });

    if (result.success) {
      setPromptResult(result.result ?? null);
      setShowSuccess(true);
    }

    setIsGenerating(false);
  };

  const handleClose = () => onClose();

  // Show loading overlay during generation or success
  if (isGenerating || showSuccess) {
    const message = formatSuccessMessage(isGenerating, getNewPromptTypes(promptResult, { isUsernameEnabled }));

    return <LoadingOverlay fullScreen message={message} spinnerSize="large" />;
  }

  // Handle error state with dialog
  const hasError = !!hookErrors?.length;

  const errorProps = hookErrors?.length
    ? {
        title: 'Error',
        message: formatErrorMessages(hookErrors),
        correlationId: getCorrelationId(hookErrors[0]),
        onRetry: handleRegenerate,
        onBack: handleClose,
      }
    : null;

  return (
    <>
      <Dialog
        cancelAction={{
          label: 'Cancel',
          disabled: false,
        }}
        description="Generate safety prompts when automatic generation fails or no prompt exists for this community."
        isOpen={isOpen && !hasError}
        primaryAction={{
          label: 'Generate',
          onClick: handleRegenerate,
          variant: 'ai',
        }}
        size="md"
        title="Generate Safety Prompts"
        onClose={onClose}
      >
        <div className={styles.confirmationMessage}>
          <p>
            Classification prompts are automatically generated when label definitions or community metadata (description, PG rating, genres, guidelines) change.
            Use manual generation if automatic generation failed or to create new prompt versions for testing.
          </p>
        </div>
      </Dialog>

      {/* Error Dialog */}
      {errorProps && <ErrorDisplay isOpen={hasError} size="md" onClose={handleClose} {...errorProps} />}
    </>
  );
}

export const RegeneratePromptDialog = withAppInsightsTracking(RegeneratePromptDialogComponent, 'RegeneratePromptDialog');
